<template>
	<div class="message-viewport">
		<div class="im-chat-main" id="chatScrollBox" ref="chatScrollBox" @scroll="$emit('scroll', $event)">
			<button
				v-if="historyStatusType === 'error'"
				type="button"
				class="message-history-status is-error"
				@click="$emit('retry-history')"
			>
				<i class="el-icon-refresh-right"></i>
				<span>加载失败，点击重试</span>
			</button>
			<div
				v-else-if="historyStatusType"
				class="message-history-status"
				:class="`is-${historyStatusType}`"
				role="status"
				aria-live="polite"
			>
				<i :class="historyStatusIcon"></i>
				<span>{{ historyStatusText }}</span>
			</div>
			<div
				v-if="bottomStatusType"
				class="message-history-status is-bottom"
				:class="`is-${bottomStatusType}`"
				role="status"
				aria-live="polite"
			>
				<i :class="bottomStatusIcon"></i>
				<span>{{ bottomStatusText }}</span>
			</div>
			<empty-state
				v-if="showMessages.length === 0"
				class="message-empty-state"
				icon="el-icon-chat-dot-round"
				title="还没有消息"
				hint="发送第一条消息，开始这段对话"
			></empty-state>
			<div v-else class="message-track">
				<div class="im-chat-box" :class="{ 'multi-select-mode': isMultiSelectMode }">
				<div
					v-for="(msgInfo, idx) in showMessages"
					:key="messageKey(msgInfo, idx)"
					:class="{ 'message-selected': isMessageSelected(msgInfo) }"
					@click="$emit('message-click', $event, msgInfo)"
				>
					<div v-if="isMultiSelectMode && isAllowSelectMessage(msgInfo)" class="message-checkbox" @click.stop>
						<el-checkbox :value="isMessageSelected(msgInfo)" @change="$emit('toggle-message-select', msgInfo)"></el-checkbox>
					</div>
					<chat-message-item
						:id="msgInfo.id"
						:active="activeMessageIdx == showMinIdx + idx || isMessageSelected(msgInfo)"
						:mine="msgInfo.sendId == mineId"
						:headImage="headImage(msgInfo)"
						:showName="showName(msgInfo)"
						:msgInfo="msgInfo"
						:quoteShowName="showName(msgInfo.quoteMessage)"
						:group="group"
						:groupMemberMap="groupMemberMap"
						:groupMembers="groupMembers"
						@call="$emit('call', msgInfo.type)"
						@locateQuote="$emit('locate-quote', $event)"
						@copy="$emit('copy', $event)"
						@resend="$emit('resend', $event)"
						@delete="$emit('delete', $event)"
						@recall="$emit('recall', $event)"
						@quote="$emit('quote', $event)"
						@top="$emit('top', $event)"
						@forward="$emit('forward', $event)"
						@multiSelect="$emit('multi-select', $event)"
						@addSticker="$emit('add-sticker', $event)"
						@download="$emit('download', $event)"
						@edit="$emit('edit', $event)"
					></chat-message-item>
				</div>
			</div>
			</div>
		</div>
	</div>
</template>

<script>
import ChatMessageItem from "./ChatMessageItem.vue";

export default {
	name: "messageViewport",
	components: {
		ChatMessageItem
	},
	props: {
		activeMessageIdx: {
			type: Number,
			default: -1
		},
		group: {
			type: Object,
			default: () => ({})
		},
		groupMemberMap: {
			type: Object,
			default: () => ({})
		},
		groupMembers: {
			type: Array,
			default: () => []
		},
		headImage: {
			type: Function,
			default: () => ""
		},
		isAllowSelectMessage: {
			type: Function,
			default: () => false
		},
		isMessageSelected: {
			type: Function,
			default: () => false
		},
		isMultiSelectMode: {
			type: Boolean,
			default: false
		},
		historyStatusType: {
			type: String,
			default: ''
		},
		bottomStatusType: {
			type: String,
			default: ''
		},
		mineId: {
			type: [Number, String],
			default: null
		},
		showMessages: {
			type: Array,
			default: () => []
		},
		showMinIdx: {
			type: Number,
			default: 0
		},
		showName: {
			type: Function,
			default: () => ""
		}
	},
	computed: {
		historyStatusText() {
			if (this.historyStatusType === 'loading') {
				return '正在加载更早消息...';
			}
			if (this.historyStatusType === 'finished') {
				return '没有更早消息了';
			}
			return '';
		},
		historyStatusIcon() {
			if (this.historyStatusType === 'loading') {
				return 'el-icon-loading';
			}
			if (this.historyStatusType === 'finished') {
				return 'el-icon-check';
			}
			return '';
		},
		bottomStatusText() {
			if (this.bottomStatusType === 'loading') {
				return '正在加载较新消息...';
			}
			if (this.bottomStatusType === 'finished') {
				return '已是最新消息';
			}
			return '';
		},
		bottomStatusIcon() {
			if (this.bottomStatusType === 'loading') {
				return 'el-icon-loading';
			}
			if (this.bottomStatusType === 'finished') {
				return 'el-icon-check';
			}
			return '';
		}
	},
	methods: {
		getScrollElement() {
			return this.$refs.chatScrollBox;
		},
		messageKey(msgInfo, idx) {
			if (!msgInfo) {
				return `message-empty-${this.showMinIdx + idx}`;
			}
			// localId 在发送、确认和历史合并时保持不变，避免回填服务端 id 后重建媒体组件。
			if (msgInfo.localId) {
				return `message-local-${msgInfo.localId}`;
			}
			if (msgInfo.id != null && msgInfo.id !== '') {
				return `message-id-${msgInfo.id}`;
			}
			// 成功入库后优先使用服务端 id，避免历史消息因复用 tmpId 产生重复 key。
			if (msgInfo.selfSend && msgInfo.tmpId != null && msgInfo.tmpId !== '') {
				return `message-tmp-${msgInfo.tmpId}`;
			}
			if (msgInfo.tmpId != null && msgInfo.tmpId !== '') {
				return `message-tmp-${msgInfo.tmpId}`;
			}
			return `message-fallback-${msgInfo.sendTime || 0}-${msgInfo.type || 'unknown'}-${this.showMinIdx + idx}`;
		}
	}
}
</script>

<style scoped lang="scss">
.message-viewport {
	--message-side-gap: 16px;
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;

	.im-chat-main {
		flex: 1;
		overflow-y: auto;
		overscroll-behavior: contain;
		position: relative;
		box-sizing: border-box;
		padding: 8px var(--message-side-gap) 10px;
		background: var(--im-chat-bg);

		.message-history-status {
			position: absolute;
			left: 50%;
			top: 10px;
			z-index: 12;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			gap: 6px;
			min-height: 28px;
			max-width: min(280px, calc(100% - 32px));
			padding: 0 12px;
			border: 1px solid rgba(var(--im-black-rgb), 0.06);
			border-radius: 999px;
			background: rgba(var(--im-white-rgb), 0.92);
			box-shadow: 0 6px 18px rgba(var(--im-black-rgb), 0.08);
			color: var(--im-text-color-secondary);
			font-size: 12px;
			line-height: 18px;
			white-space: nowrap;
			transform: translateX(-50%);
			backdrop-filter: blur(8px);
			pointer-events: none;

			&.is-bottom {
				top: auto;
				bottom: 12px;
			}

			i {
				font-size: 13px;
				color: var(--im-color-primary);
			}

			&.is-finished i {
				color: var(--im-color-success);
			}

			&.is-error {
				border: 1px solid rgba(var(--im-danger-rgb), 0.16);
				background: rgba(var(--im-white-rgb), 0.96);
				color: var(--im-color-danger);
				cursor: pointer;
				pointer-events: auto;

				i {
					color: var(--im-color-danger);
				}
			}
		}

		.message-empty-state {
			min-height: 100%;
		}

		.message-track {
			width: 100%;
			max-width: none;
			margin: 0;
			padding: 0;
		}

		.im-chat-box {
			position: relative;

			&.multi-select-mode {
				> div {
					position: relative;
					cursor: pointer;
					padding-left: 40px;
					transition: background-color 0.2s;

					&:hover {
						background-color: rgba(var(--im-primary-rgb), 0.04);
					}

					&.message-selected {
						background-color: rgba(var(--im-primary-rgb), 0.10);
					}
				}

				.message-checkbox {
					position: absolute;
					left: 8px;
					top: 50%;
					transform: translateY(-50%);
					z-index: 10;
				}
			}
		}
	}

	@media (max-width: 640px) {
		--message-side-gap: 10px;

		.im-chat-main {
			padding-top: 8px;
			padding-bottom: 10px;
		}
	}
}
</style>
